import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { certificateAPI, tenantAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

const CertificatePage = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  
  const queryClient = useQueryClient();

  // 获取证件列表
  const { data, isLoading, error } = useQuery(
    ['certificates', { status: statusFilter, search, page }],
    () => certificateAPI.getAll({
      status: statusFilter,
      search,
      page,
      per_page: 10
    })
  );

  // 创建证件
  const createMutation = useMutation(
    (data) => certificateAPI.create(data),
    {
      onSuccess: () => {
        toast.success('安全证件创建成功');
        setShowCreateModal(false);
        queryClient.invalidateQueries('certificates');
        queryClient.invalidateQueries('dashboardOverview');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '创建失败');
      }
    }
  );

  // 更新证件
  const updateMutation = useMutation(
    ({ id, data }) => certificateAPI.update(id, data),
    {
      onSuccess: () => {
        toast.success('安全证件更新成功');
        setShowEditModal(false);
        setSelectedCertificate(null);
        queryClient.invalidateQueries('certificates');
        queryClient.invalidateQueries('dashboardOverview');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '更新失败');
      }
    }
  );

  // 删除证件
  const deleteMutation = useMutation(
    (id) => certificateAPI.delete(id),
    {
      onSuccess: () => {
        toast.success('安全证件删除成功');
        queryClient.invalidateQueries('certificates');
        queryClient.invalidateQueries('dashboardOverview');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '删除失败');
      }
    }
  );

  const handleCreate = (data) => {
    createMutation.mutate(data);
  };

  const handleUpdate = (id, data) => {
    updateMutation.mutate({ id, data });
  };

  const handleDelete = (id) => {
    if (window.confirm('确定要删除这个安全证件吗？')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (certificate) => {
    setSelectedCertificate(certificate);
    setShowEditModal(true);
  };

  const certificates = data?.certificates || [];
  const pagination = data ? {
    total: data.total,
    page: data.page,
    per_page: data.per_page,
    pages: data.pages
  } : null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      valid: { label: '正常', color: 'badge-success' },
      expiring: { label: '即将过期', color: 'badge-warning' },
      expired: { label: '已过期', color: 'badge-danger' },
    };
    
    const config = statusConfig[status] || { label: status, color: 'badge-gray' };
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '未设置';
    try {
      return new Date(dateString).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return '无效日期';
    }
  };

  // 计算证件状态提醒
  const expiringCount = certificates.filter(c => c.status === 'expiring').length;
  const expiredCount = certificates.filter(c => c.status === 'expired').length;

  return (
    <div className="space-y-6">
      {/* 页面标题和操作按钮 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">安全证件管理</h1>
          <p className="text-gray-600 mt-1">管理员工安全证件和证书</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="mt-4 sm:mt-0 btn-primary"
        >
          <i className="fa fa-plus mr-2"></i>
          上传证件
        </button>
      </div>

      {/* 状态提醒 */}
      {(expiringCount > 0 || expiredCount > 0) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="fa fa-exclamation-triangle text-yellow-400 mt-0.5"></i>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-yellow-700">
                您有 <strong>{expiringCount}</strong> 个证件即将过期，
                <strong>{expiredCount}</strong> 个证件已过期，请及时处理。
              </p>
            </div>
            <div className="ml-auto">
              <button
                onClick={() => {
                  setStatusFilter('expiring');
                  toast.success('已筛选出即将过期的证件');
                }}
                className="text-sm font-medium text-primary hover:text-blue-700"
              >
                查看详情
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 搜索和过滤 */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索证件..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-input pl-10"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fa fa-search text-gray-400"></i>
              </div>
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">所有状态</option>
              <option value="valid">正常</option>
              <option value="expiring">即将过期</option>
              <option value="expired">已过期</option>
            </select>
          </div>
        </div>
      </div>

      {/* 证件列表 */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner mx-auto"></div>
            <p className="mt-4 text-gray-600">加载安全证件中...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <i className="fa fa-exclamation-circle text-red-500 mr-3"></i>
            <div>
              <p className="text-red-800 font-medium">加载失败</p>
              <p className="text-red-600 text-sm mt-1">无法加载安全证件列表，请稍后重试</p>
            </div>
          </div>
        </div>
      ) : certificates.length === 0 ? (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-12 text-center">
            <i className="fa fa-id-card-o text-gray-300 text-4xl mb-3"></i>
            <p className="text-gray-500">暂无安全证件</p>
            <p className="text-gray-400 text-sm mt-1">点击"上传证件"按钮开始添加</p>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      证件名称
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      持有人
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      证件编号
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      有效期至
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {certificates.map((certificate) => (
                    <tr key={certificate.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{certificate.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {certificate.holder?.full_name || '未知'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{certificate.certificate_number}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(certificate.expiry_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(certificate.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(certificate)}
                          className="text-primary hover:text-blue-700 mr-3"
                        >
                          <i className="fa fa-edit mr-1"></i>编辑
                        </button>
                        <button
                          onClick={() => handleDelete(certificate.id)}
                          className="text-danger hover:text-red-700"
                        >
                          <i className="fa fa-trash mr-1"></i>删除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 分页 */}
          {pagination && pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    显示 <span className="font-medium">{(pagination.page - 1) * pagination.per_page + 1}</span> 到{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.per_page, pagination.total)}
                    </span>{' '}
                    条，共 <span className="font-medium">{pagination.total}</span> 条记录
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, pagination.page - 1))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <i className="fa fa-chevron-left"></i>
                    </button>
                    {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                      let pageNum;
                      if (pagination.pages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.pages - 2) {
                        pageNum = pagination.pages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pagination.page === pageNum
                              ? 'z-10 bg-primary border-primary text-white'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage(Math.min(pagination.pages, pagination.page + 1))}
                      disabled={pagination.page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <i className="fa fa-chevron-right"></i>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* 创建证件模态框 */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75"></div>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
              <button
                onClick={() => setShowCreateModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <i className="fa fa-times text-xl"></i>
              </button>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">上传安全证件</h3>
              </div>
              <div className="px-6 py-4">
                <CertificateForm
                  onSubmit={handleCreate}
                  onCancel={() => setShowCreateModal(false)}
                  isLoading={createMutation.isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 编辑证件模态框 */}
      {showEditModal && selectedCertificate && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75"></div>
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCertificate(null);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <i className="fa fa-times text-xl"></i>
              </button>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">编辑安全证件</h3>
              </div>
              <div className="px-6 py-4">
                <CertificateForm
                  certificate={selectedCertificate}
                  onSubmit={(data) => handleUpdate(selectedCertificate.id, data)}
                  onCancel={() => {
                    setShowEditModal(false);
                    setSelectedCertificate(null);
                  }}
                  isLoading={updateMutation.isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 证件表单组件
const CertificateForm = ({ certificate, onSubmit, onCancel, isLoading }) => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: certificate ? {
      name: certificate.name,
      certificate_number: certificate.certificate_number || '',
      issuing_authority: certificate.issuing_authority || '',
      issue_date: certificate.issue_date ? new Date(certificate.issue_date).toISOString().split('T')[0] : '',
      expiry_date: certificate.expiry_date ? new Date(certificate.expiry_date).toISOString().split('T')[0] : '',
      status: certificate.status || 'valid',
      renewal_required: certificate.renewal_required || false,
      notes: certificate.notes || '',
      holder_id: certificate.holder?.id || '',
    } : {
      holder_id: '',
      status: 'valid',
      renewal_required: false,
    }
  });
  const [photoPreview, setPhotoPreview] = useState(certificate?.photo_url || null);

  // 获取用户列表
  const { data: usersData } = useQuery(
    'tenantUsers',
    () => tenantAPI.getUsers(),
    {
      enabled: !certificate, // 只在创建时获取
    }
  );

  const users = usersData?.users || [];

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormSubmit = (data) => {
    if (!data.holder_id) {
      toast.error('请选择证件持有人');
      return;
    }
    
    const formData = {
      ...data,
      holder_id: parseInt(data.holder_id),
      photo_url: photoPreview,
    };
    
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {/* 证件照片上传 */}
        <div>
          <label className="form-label">证件照片</label>
          <div className="flex items-center space-x-4">
            <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50">
              {photoPreview ? (
                <img src={photoPreview} alt="证件预览" className="w-full h-full object-cover" />
              ) : (
                <i className="fa fa-id-card-o text-gray-400 text-2xl"></i>
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
                id="certificate-photo"
              />
              <label
                htmlFor="certificate-photo"
                className="btn-secondary cursor-pointer inline-block"
              >
                <i className="fa fa-upload mr-2"></i>
                {photoPreview ? '更换照片' : '上传照片'}
              </label>
              {photoPreview && (
                <button
                  type="button"
                  onClick={() => setPhotoPreview(null)}
                  className="ml-2 text-danger hover:text-red-700 text-sm"
                >
                  <i className="fa fa-trash mr-1"></i>删除
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 证件持有人 */}
        {!certificate && (
          <div>
            <label className="form-label">证件持有人 *</label>
            <select
              {...register('holder_id', { required: '请选择证件持有人' })}
              className="form-input"
            >
              <option value="">请选择持有人</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name || user.username} ({user.department || user.position})
                </option>
              ))}
            </select>
            {errors.holder_id && (
              <p className="mt-1 text-sm text-danger">{errors.holder_id.message}</p>
            )}
          </div>
        )}

        <div>
          <label className="form-label">证件名称 *</label>
          <input
            type="text"
            {...register('name', { required: '请输入证件名称' })}
            className="form-input"
            placeholder="请输入证件名称"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-danger">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="form-label">证件编号</label>
          <input
            type="text"
            {...register('certificate_number')}
            className="form-input"
            placeholder="请输入证件编号"
          />
        </div>

        <div>
          <label className="form-label">发证机构</label>
          <input
            type="text"
            {...register('issuing_authority')}
            className="form-input"
            placeholder="请输入发证机构名称"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">发证日期</label>
            <input
              type="date"
              {...register('issue_date')}
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">有效期至 *</label>
            <input
              type="date"
              {...register('expiry_date', { required: '请选择有效期' })}
              className="form-input"
            />
            {errors.expiry_date && (
              <p className="mt-1 text-sm text-danger">{errors.expiry_date.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">证件状态</label>
            <select {...register('status')} className="form-input">
              <option value="valid">正常</option>
              <option value="expiring">即将过期</option>
              <option value="expired">已过期</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="renewal_required"
              {...register('renewal_required')}
              className="h-4 w-4 text-primary border-gray-300 rounded"
            />
            <label htmlFor="renewal_required" className="ml-2 block text-sm text-gray-900">
              需要续期
            </label>
          </div>
        </div>

        <div>
          <label className="form-label">备注</label>
          <textarea
            {...register('notes')}
            rows="2"
            className="form-input"
            placeholder="请输入备注信息"
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isLoading}
        >
          取消
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner mr-2"></div>
              {certificate ? '保存中...' : '创建中...'}
            </>
          ) : (
            certificate ? '保存更改' : '创建证件'
          )}
        </button>
      </div>
    </form>
  );
};

export default CertificatePage;